/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Checkbox, Button, TextField, Select, MenuItem, Paper, Dialog, DialogTitle, DialogContent, DialogActions, FormControlLabel, FormControl, InputLabel, FormHelperText } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import React, { useEffect, useState } from "react";
import { addNewProject, deleteProject, editProject, getAllClients, getProjects } from "../../../api/apiCall";
import { useAuth } from "../../../auth/auth";
import { format } from "date-fns";
import { toast } from "react-toastify";
import { Modal } from "antd";

const Projects = () => {
    const {state} = useAuth();
    const [projects, setProjects] = useState<any>([]);
    const [clients, setClients] = useState<any>([]);
    const [loading, setLoading] = useState(true);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
    const [currentProject, setCurrentProject] = useState<any>(null);
    const [formData, setFormData] = useState({
        projectName: '',
        companyId: '',
        postCode: 'N/A',
        status: true
    });
    const [formErrors, setFormErrors] = useState({
        projectName: '',
        companyId: ''
    });

    const workTypes = ['N/A', 'Price Work', 'Labour', 'X Other'];

    const handleAddClick = () => {
        setModalMode('add');
        setFormData({
            projectName: '',
            companyId: '',
            postCode: 'N/A',
            status: true
        });
        setCurrentProject(null);
        setFormErrors({ projectName: '', companyId: '' });
        setIsDialogOpen(true);
    };

    const handleEditClick = (params: any) => {
        setModalMode('edit');
        setCurrentProject(params.row);
        setFormData({
            projectName: params.row.projectName,
            companyId: params.row.companyId || '',
            postCode: params.row.postCode || 'N/A',
            status: params.row.status
        });
        setFormErrors({ projectName: '', companyId: '' });
        setIsDialogOpen(true);
    };

    const handleDialogClose = () => {
        setIsDialogOpen(false);
        setFormData({
            projectName: '',
            companyId: '',
            postCode: 'N/A',
            status: true
        });
        setCurrentProject(null);
        setFormErrors({ projectName: '', companyId: '' });
    };

    const validateForm = () => {
        const errors = {
            projectName: '',
            companyId: ''
        };
        let isValid = true;

        if (!formData.projectName.trim()) {
            errors.projectName = 'Project name cannot be empty';
            isValid = false;
        }

        if (!formData.companyId) {
            errors.companyId = 'Please select a client';
            isValid = false;
        }

        setFormErrors(errors);
        return isValid;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        if (modalMode === 'add') {
            const response = await addNewProject(
                state.user?.token, 
                formData.projectName, 
                formData.companyId, 
                formData.postCode
            );
            if (response) {
                toast.success(response.data);
                getAllProjects();
                handleDialogClose();
            } else {
                toast.error('Something went wrong!');
            }
        } else {
            if (currentProject) {
                const response = await editProject(state.user?.token, {
                    id: currentProject.id,
                    projectName: formData.projectName,
                    companyId: formData.companyId,
                    status: formData.status,
                    postCode: formData.postCode
                });
                if (response === 403) {
                    toast.warn('You are not authorized!');
                } else if (response) {
                    toast.success(response.data);
                    getAllProjects();
                    handleDialogClose();
                }
            }
        }
    };

    const handleDelete = async (id: any) => {
        try {
            const response: any = await deleteProject(state.user?.token, id);
            if(response?.forbidden) {
                toast.warn('You don\'t have permission to delete this project');
                return;
            }
        } catch (error) {
            console.error("Failed to fetch clients:", error);
        } finally {
            getAllProjects();
        }
    };

    const getClientsList = async () => {
        const response = await getAllClients(state.user?.token);
        if(response){
            setClients(response);
        }
    };

    const getAllProjects = async () => {
        try {
            setLoading(true);
            const response = await getProjects(state.user?.token);
            if (response) {
                setProjects(response);
            }
        } catch (error) {
            console.error("Failed to fetch projects:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(()=> {
        getAllProjects();
        getClientsList();
    }, []);

    const columns: any = [
        {
            field: "projectName",
            headerName: "Project Name",
            flex: 0.4,
            renderCell: (params: any) => (
                <div>{params.row.projectName}</div>
            ),
        },
        {
            field: "companyId",
            headerName: "Client",
            flex: 0.4,
            renderCell: (params: any) => {
                const client = clients.find((c: any) => c.id === params.row.companyId);
                return <div>{client?.companyName || 'N/A'}</div>;
            },
        },
        {
            field: "postCode",
            headerName: "Type of Work",
            flex: 0.2,
            renderCell: (params: any) => (
                <div>{params.row.postCode || 'N/A'}</div>
            ),
        },
        {
            field: "createdDate",
            headerName: "Created Date",
            flex: 0.2,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params: any) => (
                <div>
                    {format(new Date(params.row.createdDate || Date.now()), 'dd/MM/yyyy')}
                </div>
            ),
        },
        {
            field: "status",
            headerName: "Status",
            flex: 0.2,
            renderCell: (params: any) => (
                <div style={{color: params.row.status ? 'green' : 'red'}}>
                    {params.row.status ? 'Active' : 'Inactive'}
                </div>
            ),
        },
        {
            field: "actions",
            headerName: "Actions",
            flex: 0.3,
            renderCell: (params: any) => (
                <div>
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={() => handleEditClick(params)}
                        style={{ marginRight: 8, backgroundColor: "#6c757d", boxShadow: "none" }}
                    >
                        Edit
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        size="small"
                        style={{boxShadow: "none"}}
                        onClick={() => 
                            Modal.confirm({
                                title: `Are you sure you want to delete ${params.row.projectName}?`,
                                 onOk: () => handleDelete(params.row.id),
                                okButtonProps: { danger: true },
                                okText: 'YES',
                                cancelText: 'NO',
                                centered: true
                            })
                        }
                    >
                        Delete
                    </Button>
                </div>
            )
        }
    ];

    return (
        <Box sx={{ 
            p: 1,
            height: '100%',
            minHeight: '100vh'
        }}>
            <Paper 
                elevation={0} 
                sx={{ 
                    p: 3, 
                    borderRadius: '16px',
                    backgroundColor: '#ffffff',
                    maxWidth: '1200px',
                    margin: '0 auto'
                }}
            >
                <Box
                    sx={{ 
                        display: 'flex', 
                        justifyContent: 'flex-end', 
                        alignItems: 'center',
                        mb: 3 
                    }}
                >
                    <Button
                        variant="contained"
                        onClick={handleAddClick}
                        style={{boxShadow: 'none'}}
                    >
                        New Project
                    </Button>
                </Box>
                <Box 
                    sx={{
                        width: '100%',
                        margin:'0 auto',
                        height: '740px',
                        "@media (max-width: 800px)": {
                            margin: 1,
                            minWidth: "95%",
                        },
                    }}>
                    <DataGrid
                        rows={projects}
                        columns={columns}
                        loading={loading}
                        disableRowSelectionOnClick
                        sx={{
                            "@media (max-width: 700px)": {
                                width: "100%",
                                overflowX: "scroll",
                            },
                            "& .MuiDataGrid-columnHeader": {
                                color: "#333",
                                fontWeight: "bold",
                                fontSize: 15,
                                backgroundColor: '#f0f0f0',
                            },
                            "& .MuiDataGrid-columnHeaderTitle": {
                                fontWeight: "bold",
                            },
                            "@media (max-width: 600px)": {
                                fontSize: "0.7rem",
                            },
                        }}
                    />
                </Box>
            </Paper>

            <Dialog
                open={isDialogOpen} 
                onClose={handleDialogClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {modalMode === 'add' ? 'Add New Project' : 'Edit Project'}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        size="small"
                        margin="dense"
                        label="Project Name"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={formData.projectName}
                        onChange={(e) => {
                            setFormData({...formData, projectName: e.target.value});
                            setFormErrors({...formErrors, projectName: ''});
                        }}
                        error={!!formErrors.projectName}
                        helperText={formErrors.projectName}
                        sx={{ mt: 2, mb: 2 }}
                    />

                    <FormControl fullWidth margin="dense" error={!!formErrors.companyId}>
                        <InputLabel>Client</InputLabel>
                        <Select
                            size="small"
                            value={formData.companyId}
                            label="Client"
                            onChange={(e) => {
                                setFormData({...formData, companyId: e.target.value});
                                setFormErrors({...formErrors, companyId: ''});
                            }}
                            sx={{ mb: 2 }}
                        >
                            <MenuItem value="" disabled>Select a client</MenuItem>
                            {clients.map((client: any) => (
                                <MenuItem key={client.id} value={client.id}>
                                    {client.companyName}
                                </MenuItem>
                            ))}
                        </Select>
                        {formErrors.companyId && (
                            <FormHelperText>{formErrors.companyId}</FormHelperText>
                        )}
                    </FormControl>

                    <FormControl fullWidth margin="dense">
                        <InputLabel>Type of Work</InputLabel>
                        <Select
                            size="small"
                            value={formData.postCode}
                            label="Type of Work"
                            onChange={(e) => setFormData({...formData, postCode: e.target.value})}
                            sx={{ mb: 2 }}
                        >
                            {workTypes.map((type) => (
                                <MenuItem key={type} value={type}>
                                    {type}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={formData.status}
                                onChange={(e) => setFormData({...formData, status: e.target.checked})}
                                color="primary"
                            />
                        }
                        label="Active Status"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose} color="inherit">
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit} color="primary" variant="contained" style={{boxShadow: 'none'}}>
                        {modalMode === 'add' ? 'Add' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Projects;